import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const DataSourceBadge = ({ source, showTooltip = true, size = 'sm' }) => {
  const { t } = useLanguage();
  
  const DATA_SOURCES = {
    crux: {
      label: t('ui.dataSource.cruxFresh'),
      color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      dot: 'bg-emerald-400',
      description: t('ui.dataSource.cruxFreshDesc')
    },
    cache: {
      label: t('ui.dataSource.cacheLocal'),
      color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      dot: 'bg-blue-400',
      description: t('ui.dataSource.cacheLocalDesc')
    },
    pagespeed: {
      label: t('ui.dataSource.pageSpeedLighthouse'),
      color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      dot: 'bg-amber-400',
      description: t('ui.dataSource.pageSpeedLighthouseDesc')
    },
    bigquery: {
      label: t('ui.dataSource.history'),
      color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      dot: 'bg-purple-400',
      description: t('ui.dataSource.historyDesc')
    }
  };
  
  const config = DATA_SOURCES[source];
  if (!config) return null;

  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5 gap-1',
    sm: 'text-xs px-2 py-1 gap-1.5',
    md: 'text-sm px-3 py-1.5 gap-2'
  };

  const dotSizes = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5'
  };

  return (
    <div 
      className={`inline-flex items-center rounded-full border ${config.color} ${sizeClasses[size]}`}
      title={showTooltip ? config.description : undefined}
    >
      <span className={`${dotSizes[size]} rounded-full ${config.dot} animate-pulse`}></span>
      <span className="font-medium">{config.label}</span>
    </div>
  );
};

const DataSourceInfo = ({ 
  source, 
  fromCache = false, 
  isStale = false, 
  collectionPeriod,
  className = ''
}) => {
  const { t, language } = useLanguage();
  
  let effectiveSource = source;
  
  if (fromCache && !isStale) {
    effectiveSource = 'cache';
  } else if (source === 'lighthouse' || source === 'psi') {
    effectiveSource = 'pagespeed';
  } else if (source === 'bigquery' || source === 'bigquery_monthly') {
    effectiveSource = 'bigquery';
  } else if (source === 'crux' || !source) {
    effectiveSource = fromCache ? 'cache' : 'crux';
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      const locale = language === 'cs' ? 'cs-CZ' : 'en-US';
      return date.toLocaleDateString(locale, { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <DataSourceBadge source={effectiveSource} />
      {collectionPeriod && (
        <span className="text-xs text-slate-500">
          {t('ui.dataSource.update')} {formatDate(collectionPeriod)}
        </span>
      )}
      {isStale && (
        <span className="text-xs text-amber-400/80 bg-amber-500/10 px-2 py-0.5 rounded">
          {t('ui.dataSource.stale')}
        </span>
      )}
    </div>
  );
};

export { DataSourceBadge, DataSourceInfo };
export default DataSourceBadge;
